import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Shield, UserMinus, UserPlus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import type { Id } from "convex/_generated/dataModel";
import { useModeratorRole, type ModeratorRole } from "../../hooks/useModeratorRole";
import { useCurrentUser } from "../../hooks/useCurrentUser";

// Returns true if the caller is allowed to remove the target based on role hierarchy.
function canRemove(callerRole: ModeratorRole, targetRole: string): boolean {
  if (callerRole === "admin") return true;
  if (callerRole === "moderator") return targetRole === "volunteer";
  return false;
}

// Returns the roles the caller is allowed to assign.
function assignableRoles(callerRole: ModeratorRole): Array<"admin" | "moderator" | "volunteer"> {
  if (callerRole === "admin") return ["admin", "moderator", "volunteer"];
  return ["volunteer"];
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  moderator: "Moderator",
  volunteer: "Volunteer",
};

type ModeratorEntry = {
  _id: string;
  userId: Id<"users">;
  role: string;
  isActive: boolean;
  addedAt: number;
  user?: { _id: Id<"users">; name?: string; email: string } | null;
};

export function ModeratorManager() {
  const moderators = useQuery(api.moderation.getModerators);
  const addModerator = useMutation(api.moderation.addModerator);
  const removeModerator = useMutation(api.moderation.removeModerator);

  const { role: myRole } = useModeratorRole();
  const { user: currentUser } = useCurrentUser();

  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState<"admin" | "moderator" | "volunteer">("volunteer");
  const searchResults = useQuery(api.moderation.searchUsers, { search });

  const roles = assignableRoles(myRole);

  const handleAdd = async (userId: Id<"users">) => {
    // moderators always add as volunteer regardless of selectedRole
    const role = myRole === "admin" ? selectedRole : "volunteer";
    await addModerator({ userId, role });
  };

  const handleRemove = async (userId: Id<"users">) => {
    await removeModerator({ userId });
  };

  const moderatorList = (moderators as ModeratorEntry[] | undefined) ?? [];
  const moderatorUserIds = new Set(
    moderatorList.filter((m) => m.isActive).map((m) => m.userId)
  );

  return (
    <section>
      <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5" />
        Moderators
      </h2>

      {/* Current moderators */}
      <div className="mb-6">
        <p className="text-sm text-muted-foreground mb-3">Active moderators</p>
        {moderators === undefined ? (
          <div className="h-16 bg-secondary/30 rounded-lg animate-pulse" />
        ) : moderatorList.filter((m) => m.isActive).length === 0 ? (
          <p className="text-sm text-muted-foreground italic">None yet.</p>
        ) : (
          <div className="space-y-2">
            {moderatorList
              .filter((m) => m.isActive)
              .map((m) => {
                const isSelf = currentUser?._id === m.userId;
                const showRemove = !isSelf && canRemove(myRole, m.role);
                return (
                  <div
                    key={m._id}
                    className="flex items-center justify-between bg-card rounded-lg border border-border/50 px-4 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-foreground truncate">
                        {m.user?.name || m.user?.email || "Unknown"}
                      </span>
                      {m.user?.email && m.user?.name && (
                        <span className="text-xs text-muted-foreground truncate hidden sm:inline">
                          {m.user.email}
                        </span>
                      )}
                      {/* Admins get an inline role selector; everyone else sees a static badge */}
                      {myRole === "admin" && !isSelf ? (
                        <select
                          value={m.role}
                          onChange={(e) =>
                            addModerator({
                              userId: m.userId,
                              role: e.target.value as "admin" | "moderator" | "volunteer",
                            })
                          }
                          className="text-xs font-medium px-2 py-0.5 rounded-full border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
                        >
                          <option value="admin">Admin</option>
                          <option value="moderator">Moderator</option>
                          <option value="volunteer">Volunteer</option>
                        </select>
                      ) : (
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                            m.role === "admin"
                              ? "bg-primary/10 text-primary"
                              : m.role === "moderator"
                              ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                              : "bg-secondary text-muted-foreground"
                          }`}
                        >
                          {ROLE_LABELS[m.role] ?? m.role}
                        </span>
                      )}
                      {isSelf && (
                        <span className="text-xs text-muted-foreground">(you)</span>
                      )}
                    </div>
                    {showRemove && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive flex-shrink-0"
                        onClick={() => handleRemove(m.userId)}
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Invite by user search */}
      <div>
        <p className="text-sm text-muted-foreground mb-3">
          {myRole === "admin" ? "Add a moderator" : "Add a volunteer"}
        </p>

        <div className="flex gap-2 mb-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name..."
            className="flex-1"
          />
          {/* Role selector — only shown to admins since moderators can only add volunteers */}
          {myRole === "admin" && roles.length > 1 && (
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as typeof selectedRole)}
              className="px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          )}
        </div>

        {search.trim() && searchResults !== undefined && (
          <div className="space-y-2">
            {searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users found.</p>
            ) : (
              (searchResults as Array<{ _id: Id<"users">; name?: string; email: string; verificationStatus: string }>).map((user) => {
                const alreadyMod = moderatorUserIds.has(user._id);
                return (
                  <div
                    key={user._id}
                    className="flex items-center justify-between bg-card rounded-lg border border-border/50 px-4 py-2"
                  >
                    <div>
                      <span className="text-sm text-foreground">
                        {user.name || user.email}
                      </span>
                      {user.name && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {user.email}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-2 capitalize">
                        · {user.verificationStatus}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={alreadyMod}
                      onClick={() => handleAdd(user._id)}
                      className={alreadyMod ? "opacity-50" : ""}
                    >
                      <UserPlus className="w-4 h-4" />
                      {alreadyMod
                        ? "Already mod"
                        : myRole === "admin"
                        ? `Add as ${ROLE_LABELS[selectedRole]}`
                        : "Add as Volunteer"}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </section>
  );
}
